import { useState, useEffect } from 'react'
import { useModal } from '../../contexts/ModalContext'
import { useAuth } from '../../contexts/AuthContext'
import { useCampaign } from '../../contexts/CampaignContext'
import AdminControls from '../AdminControls'
import QuestForm from '../forms/QuestForm'
import MarkdownText from '../MarkdownText'
import QuestSteps, { parseSteps } from '../QuestSteps'
import ViewToggle, { usePersistedViewMode } from '../ViewToggle'
import EntityTable from '../EntityTable'
import { formatStepProgress } from '../../utils/questProgress'

function QuestsTab({ focusId, onConsumeFocus }) {
  const [quests, setQuests] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [editingQuest, setEditingQuest] = useState(null)
  const [isCreating, setIsCreating] = useState(false)
  const [selectedQuest, setSelectedQuest] = useState(null)
  const [filterText, setFilterText] = useState('')
  const [hideCompleted, setHideCompleted] = useState(false)
  const [prefsLoaded, setPrefsLoaded] = useState(false)
  const [viewMode, setViewMode] = usePersistedViewMode('view:quests')
  const { user, authEpoch } = useAuth()
  const { canEdit } = useCampaign()
  const { alert: showAlert, confirm } = useModal()

  // Open a specific quest when navigated to via a [[wiki-link]].
  useEffect(() => {
    if (!focusId || loading) return
    const target = quests.find((q) => q.id === focusId)
    if (target) {
      setSelectedQuest(target)
      onConsumeFocus?.()
    }
  }, [focusId, loading, quests])

  useEffect(() => {
    if (!user) return

    const fetchPreferences = async () => {
      try {
        const response = await fetch('/api/preferences')
        if (!response.ok) return
        const data = await response.json()
        if (typeof data.hide_completed_quests === 'number') {
          setHideCompleted(data.hide_completed_quests === 1)
        }
      } catch (err) {
        console.error('Failed to load preferences:', err)
      } finally {
        setPrefsLoaded(true)
      }
    }

    fetchPreferences()
    // authEpoch: see the identical comment on the quests-fetch effect below —
    // if this GET (not the quests GET beside it) is the one that 401'd,
    // hideCompleted must not silently stay at its default after re-auth.
  }, [user, authEpoch])

  useEffect(() => {
    if (!user || !prefsLoaded) return

    const savePreferences = async () => {
      try {
        await fetch('/api/preferences', {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ hide_completed_quests: hideCompleted ? 1 : 0 })
        })
      } catch (err) {
        console.error('Failed to save preferences:', err)
      }
    }

    savePreferences()
  }, [hideCompleted, user, prefsLoaded])

  // authEpoch: see the identical comment in ItemsTab.jsx.
  useEffect(() => {
    fetchQuests()
  }, [authEpoch])

  useEffect(() => {
    if (!selectedQuest) return

    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        setSelectedQuest(null)
      }
    }

    document.addEventListener('keydown', handleEscape)
    document.body.style.overflow = 'hidden'

    return () => {
      document.removeEventListener('keydown', handleEscape)
      document.body.style.overflow = 'unset'
    }
  }, [selectedQuest])

  const fetchQuests = () => {
    // See ItemsTab.jsx's fetchItems for why: a non-OK response still has a
    // JSON body, so this must not hand it to setQuests() unguarded, and a
    // successful retry must clear any previous error.
    fetch('/api/quests')
      .then(res => (res.ok ? res.json() : Promise.reject(new Error('Failed to load quests'))))
      .then(data => {
        setQuests(Array.isArray(data) ? data : [])
        setError(null)
        setLoading(false)
      })
      .catch(err => {
        setError(err.message)
        setLoading(false)
      })
  }

  const handleEdit = (quest) => {
    setEditingQuest(quest)
  }

  const handleCreate = () => {
    setIsCreating(true)
  }

  // Live step-status toggle (admin, during a session). Updates the steps JSON
  // via PATCH and reflects it locally without a full refetch.
  const handleStepStatus = async (quest, index, status) => {
    const steps = parseSteps(quest.steps)
    if (!steps[index]) return
    steps[index] = { ...steps[index], status }
    const stepsJson = JSON.stringify(steps)

    try {
      const response = await fetch('/api/quests', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: quest.id, steps: stepsJson })
      })

      if (response.ok) {
        setQuests(qs => qs.map(q => (q.id === quest.id ? { ...q, steps: stepsJson } : q)))
        setSelectedQuest(sq => (sq && sq.id === quest.id ? { ...sq, steps: stepsJson } : sq))
      } else {
        const error = await response.json()
        showAlert({ title: 'Error', message: error.error || 'Failed to update objective', type: 'error' })
      }
    } catch (err) {
      showAlert({ title: 'Error', message: 'Failed to update objective', type: 'error' })
    }
  }

  const handleFormSubmit = async (formData) => {
    try {
      const isEdit = !!editingQuest
      const response = await fetch('/api/quests', {
        method: isEdit ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(isEdit ? { ...formData, id: editingQuest.id } : formData)
      })

      if (response.ok) {
        await fetchQuests()
        showAlert({
          title: isEdit ? 'Quest Updated' : 'Quest Created',
          message: `Quest "${formData.title}" has been ${isEdit ? 'updated' : 'created'} successfully.`,
          type: 'success'
        })
        setEditingQuest(null)
        setIsCreating(false)
      } else {
        const error = await response.json()
        showAlert({
          title: 'Error',
          message: error.error || `Failed to ${isEdit ? 'update' : 'create'} quest`,
          type: 'error'
        })
      }
    } catch (err) {
      showAlert({
        title: 'Error',
        message: `Failed to ${editingQuest ? 'update' : 'create'} quest`,
        type: 'error'
      })
    }
  }

  const handleDelete = async (quest) => {
    await confirm({
      title: 'Delete Quest',
      message: `Are you sure you want to delete "${quest.title}"? This action cannot be undone.`,
      confirmText: 'Delete',
      cancelText: 'Cancel',
      onConfirm: async () => {
        try {
          const response = await fetch('/api/quests', {
            method: 'DELETE',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: quest.id })
          })

          if (response.ok) {
            setQuests(quests.filter(q => q.id !== quest.id))
            showAlert({
              title: 'Quest Deleted',
              message: `"${quest.title}" has been deleted.`,
              type: 'success'
            })
          } else {
            const error = await response.json()
            showAlert({
              title: 'Error',
              message: error.error || 'Failed to delete quest',
              type: 'error'
            })
          }
        } catch (err) {
          showAlert({
            title: 'Error',
            message: 'Failed to delete quest',
            type: 'error'
          })
        }
      }
    })
  }

  const handleToggleVisibility = async (quest) => {
    const action = quest.is_visible ? 'hide' : 'reveal'
    await confirm({
      title: `${action === 'hide' ? 'Hide' : 'Reveal'} Quest`,
      message: `${action === 'hide' ? 'Hide' : 'Reveal'} "${quest.title}" ${action === 'hide' ? 'from' : 'to'} players?`,
      confirmText: action === 'hide' ? 'Hide' : 'Reveal',
      onConfirm: async () => {
        try {
          const response = await fetch('/api/quests', {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: quest.id, is_visible: quest.is_visible ? 0 : 1 })
          })

          if (response.ok) {
            setQuests(quests.map(q =>
              q.id === quest.id ? { ...q, is_visible: quest.is_visible ? 0 : 1 } : q
            ))
            showAlert({
              title: 'Visibility Updated',
              message: `"${quest.title}" is now ${action === 'hide' ? 'hidden from' : 'visible to'} players.`,
              type: 'success'
            })
          } else {
            const error = await response.json()
            showAlert({
              title: 'Error',
              message: error.error || 'Failed to update visibility',
              type: 'error'
            })
          }
        } catch (err) {
          showAlert({
            title: 'Error',
            message: 'Failed to update visibility',
            type: 'error'
          })
        }
      }
    })
  }


  if (loading) {
    return (
      <div className="tab-pane">
        <div className="page-header">
          <h1>Quests</h1>
          <p className="page-subtitle">Track your objectives and missions</p>
        </div>
        <div style={{ background: 'var(--card-bg)', padding: '3rem', borderRadius: '12px', border: '1px solid var(--border-color)', textAlign: 'center' }}>
          <p style={{ color: 'var(--text-muted)' }}>Loading quests...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="tab-pane">
        <div className="page-header">
          <h1>Quests</h1>
          <p className="page-subtitle">Track your objectives and missions</p>
        </div>
        <div style={{ background: 'var(--card-bg)', padding: '3rem', borderRadius: '12px', border: '1px solid var(--border-color)', textAlign: 'center' }}>
          <p style={{ color: '#e74c3c' }}>Error loading quests: {error}</p>
        </div>
      </div>
    )
  }

  if (quests.length === 0) {
    return (
      <div className="tab-pane">
        <div className="page-header">
          <h1>Quests</h1>
          <p className="page-subtitle">Track your objectives and missions</p>
        </div>
        <div style={{ background: 'var(--card-bg)', padding: '3rem', borderRadius: '12px', border: '1px solid var(--border-color)', textAlign: 'center' }}>
          <div style={{ fontSize: '3rem', marginBottom: '1rem', opacity: 0.5 }}>⚔️</div>
          <h3 style={{ color: 'var(--text-muted)', marginBottom: '1rem' }}>No Active Quests</h3>
          <p style={{ color: 'var(--text-muted)', marginBottom: canEdit ? '1.5rem' : 0 }}>
            {canEdit ? 'Add your first quest to get this campaign started.' : 'Your DM will reveal quests as your adventure progresses.'}
          </p>
          {canEdit && (
            <button
              onClick={handleCreate}
              style={{
                background: 'linear-gradient(135deg, #1a8a6e 0%, #2ecc71 100%)',
                color: '#ffffff',
                border: '1px solid #27ae60',
                padding: '0.8rem 1.5rem',
                borderRadius: '8px',
                fontWeight: 600,
                fontSize: '0.95rem',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                boxShadow: '0 2px 8px rgba(46, 204, 113, 0.3)',
              }}
            >
              + Add New Quest
            </button>
          )}
        </div>

        <QuestForm
          isOpen={isCreating || !!editingQuest}
          onClose={() => {
            setIsCreating(false)
            setEditingQuest(null)
          }}
          quest={editingQuest}
          onSubmit={handleFormSubmit}
        />
      </div>
    )
  }

  // Filter quests based on hideCompleted toggle
  const trimmedFilter = filterText.trim()
  const isSearching = trimmedFilter.length > 0

  const filteredQuests = (hideCompleted ? quests.filter(q => q.completed !== 1) : quests)
    .filter((quest) => {
      // Players (and admins previewing as a player) never see hidden quests at all.
      if (!canEdit && !quest.is_visible) return false
      if (!isSearching) return true
      const searchTerm = trimmedFilter.toLowerCase()
      const title = quest.title?.toLowerCase() || ''
      const description = quest.description?.toLowerCase() || ''
      const questType = quest.quest_type?.toLowerCase() || ''
      const priority = quest.priority?.toLowerCase() || ''
      return (
        title.includes(searchTerm) ||
        description.includes(searchTerm) ||
        questType.includes(searchTerm) ||
        priority.includes(searchTerm)
      )
    })
    .sort((a, b) => {
      const hiddenA = !a.is_visible && !canEdit
      const hiddenB = !b.is_visible && !canEdit
      if (hiddenA !== hiddenB) return hiddenA ? 1 : -1
      return 0
    })

  // Four bounded columns in a row (Type, Priority, Status, Objectives) is
  // exactly the case where letting the table share width evenly hurts most —
  // EntityTable.css sizes each to its own content so the description keeps
  // the remainder.
  const questColumns = [
    {
      key: 'stripe',
      header: '',
      role: 'stripe',
      // The card's borderLeft. It is the only place complete / hidden /
      // active is encoded by form rather than by text, so it has to survive.
      render: (quest) => {
        const tone = quest.completed ? 'is-done' : (quest.is_visible ? '' : 'is-hidden')
        return <i className={`et-stripe ${tone}`.trim()} />
      },
    },
    {
      key: 'title',
      header: 'Quest',
      role: 'name',
      render: (quest) => {
        const isHidden = !quest.is_visible && !canEdit
        return (
          <div className="et-name">
            {isHidden ? 'Unknown Quest' : quest.title}
            {canEdit && !quest.is_visible && (
              <span style={{ marginLeft: '0.5rem', fontSize: '0.65rem', background: '#6b7f91', color: 'var(--text-primary)', padding: '0.2rem 0.5rem', borderRadius: '3px', fontWeight: 600 }}>Hidden</span>
            )}
          </div>
        )
      },
    },
    {
      key: 'type',
      header: 'Type',
      role: 'meta',
      render: (quest) => {
        const isHidden = !quest.is_visible && !canEdit
        if (isHidden) return null
        return (
          <span style={{ background: quest.quest_type === 'main' ? 'var(--accent-teal)' : '#6b7f91', color: 'var(--text-primary)', padding: '0.2rem 0.5rem', borderRadius: '4px', fontSize: '0.7rem', fontWeight: 600 }}>
            {quest.quest_type.charAt(0).toUpperCase() + quest.quest_type.slice(1)}
          </span>
        )
      },
    },
    {
      key: 'priority',
      header: 'Priority',
      role: 'meta',
      render: (quest) => {
        const isHidden = !quest.is_visible && !canEdit
        if (isHidden) return null
        return quest.priority === 'high'
          ? <span style={{ background: '#c0392b', color: 'var(--text-primary)', padding: '0.2rem 0.5rem', borderRadius: '4px', fontSize: '0.7rem', fontWeight: 600 }}>High</span>
          : <span style={{ background: 'var(--accent-deep)', color: 'var(--text-muted)', padding: '0.2rem 0.5rem', borderRadius: '4px', fontSize: '0.7rem', fontWeight: 600, border: '1px solid var(--accent-teal)' }}>Normal</span>
      },
    },
    {
      key: 'status',
      header: 'Status',
      role: 'meta',
      render: (quest) => {
        const isHidden = !quest.is_visible && !canEdit
        if (isHidden) return null
        return quest.completed === 1
          ? <span style={{ background: '#27ae60', color: 'var(--text-primary)', padding: '0.2rem 0.5rem', borderRadius: '4px', fontSize: '0.7rem', fontWeight: 600 }}>✓ Completed</span>
          : <span style={{ background: 'var(--accent-deep)', color: 'var(--text-muted)', padding: '0.2rem 0.5rem', borderRadius: '4px', fontSize: '0.7rem', fontWeight: 600, border: '1px solid var(--accent-teal)' }}>Incomplete</span>
      },
    },
    {
      key: 'objectives',
      header: 'Objectives',
      role: 'meta',
      // Something the card has no room for. No extra fog-of-war filtering
      // here: quests.js already strips hidden steps for players, so the
      // denominator matches the list QuestSteps renders in the detail modal.
      render: (quest) => {
        const isHidden = !quest.is_visible && !canEdit
        if (isHidden) return null
        const label = formatStepProgress(parseSteps(quest.steps))
        return <span className={`et-num${label === '—' ? ' is-empty' : ''}`}>{label}</span>
      },
    },
    {
      key: 'description',
      header: 'Description',
      role: 'desc',
      render: (quest) => {
        const isHidden = !quest.is_visible && !canEdit
        return isHidden
          ? 'This quest has not been revealed yet.'
          : <MarkdownText>{quest.description}</MarkdownText>
      },
    },
    {
      key: 'actions',
      header: '',
      role: 'actions',
      render: (quest) => canEdit && (
        <AdminControls
          item={quest}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onToggleVisibility={handleToggleVisibility}
          type="quest"
        />
      ),
    },
  ]

  return (
    <div className="tab-pane">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem', flexWrap: 'wrap', gap: '1rem' }}>
        <div className="page-header" style={{ marginBottom: 0, flexShrink: 0 }}>
          <h1>Quests</h1>
          <p className="page-subtitle">Track your objectives and missions</p>
        </div>
        <input
          type="text"
          placeholder="Filter quests by title, type, priority, or description..."
          value={filterText}
          onChange={(e) => setFilterText(e.target.value)}
          style={{
            flex: '1 1 260px',
            minWidth: '200px',
            padding: '0.75rem 1rem',
            background: 'var(--accent-deep)',
            border: '1px solid var(--accent-teal)',
            borderRadius: '8px',
            color: 'var(--text-primary)',
            fontSize: '0.95rem',
            fontFamily: 'inherit'
          }}
        />
        <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', flexWrap: 'wrap' }}>
          <ViewToggle value={viewMode} onChange={setViewMode} />
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', color: 'var(--text-primary)', fontSize: '0.95rem' }}>
            <input
              type="checkbox"
              checked={hideCompleted}
              onChange={(e) => setHideCompleted(e.target.checked)}
              style={{ width: '18px', height: '18px', cursor: 'pointer' }}
            />
            Hide Completed Quests
          </label>
          {canEdit && (
            <button
              onClick={handleCreate}
              style={{
                background: 'linear-gradient(135deg, #1a8a6e 0%, #2ecc71 100%)',
                color: '#ffffff',
                border: '1px solid #27ae60',
                padding: '0.8rem 1.5rem',
                borderRadius: '8px',
                fontWeight: 600,
                fontSize: '0.95rem',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                boxShadow: '0 2px 8px rgba(46, 204, 113, 0.3)',
              }}
            >
              + Add New Quest
            </button>
          )}
        </div>
      </div>

      {viewMode === 'table' ? (
        <EntityTable
          rows={filteredQuests}
          columns={questColumns}
          label="Quests"
          onRowClick={setSelectedQuest}
          isDimmed={(quest) => !quest.is_visible}
          isStruck={(quest) => quest.completed === 1}
          emptyMessage={isSearching ? 'No quests match that filter.' : 'No quests yet.'}
        />
      ) : (
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: '1.5rem' }}>
        {filteredQuests.map(quest => {
          const isHidden = !quest.is_visible && !canEdit

          return (
            <div
              key={quest.id}
              onClick={() => setSelectedQuest(quest)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  setSelectedQuest(quest)
                }
              }}
              role="button"
              tabIndex={0}
              style={{
                background: 'var(--card-bg)',
                padding: '1.5rem',
                borderRadius: '12px',
                border: '1px solid var(--border-color)',
                borderLeft: `4px solid ${quest.completed ? '#27ae60' : (quest.is_visible ? 'var(--accent-teal)' : '#6b7f91')}`,
                opacity: quest.is_visible ? (quest.completed ? 0.8 : 1) : 0.6,
                cursor: 'pointer',
                display: 'flex',
                flexDirection: 'column',
                transition: 'transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)'
                e.currentTarget.style.boxShadow = '0 8px 18px rgba(0, 0, 0, 0.25)'
                e.currentTarget.style.borderColor = 'var(--accent-teal)'
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)'
                e.currentTarget.style.boxShadow = 'none'
                e.currentTarget.style.borderColor = 'var(--border-color)'
              }}
            >
              <h3 style={{ color: 'var(--text-primary)', fontSize: '1.15rem', margin: 0, marginBottom: '0.75rem', textDecoration: quest.completed ? 'line-through' : 'none' }}>
                {isHidden ? 'Unknown Quest' : quest.title}
              </h3>
              {!isHidden && (
                <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', marginBottom: '0.75rem' }}>
                  <span style={{ background: quest.quest_type === 'main' ? 'var(--accent-teal)' : '#6b7f91', color: 'var(--text-primary)', padding: '0.2rem 0.5rem', borderRadius: '4px', fontSize: '0.7rem', fontWeight: 600 }}>
                    {quest.quest_type.charAt(0).toUpperCase() + quest.quest_type.slice(1)} Quest
                  </span>
                  {quest.priority === 'high' && (
                    <span style={{ background: '#c0392b', color: 'var(--text-primary)', padding: '0.2rem 0.5rem', borderRadius: '4px', fontSize: '0.7rem', fontWeight: 600 }}>
                      High Priority
                    </span>
                  )}
                  {quest.completed === 1 ? (
                    <span style={{ background: '#27ae60', color: 'var(--text-primary)', padding: '0.2rem 0.5rem', borderRadius: '4px', fontSize: '0.7rem', fontWeight: 600 }}>
                      ✓ Completed
                    </span>
                  ) : (
                    <span style={{ background: 'var(--accent-deep)', color: 'var(--text-muted)', padding: '0.2rem 0.5rem', borderRadius: '4px', fontSize: '0.7rem', fontWeight: 600, border: '1px solid var(--accent-teal)' }}>
                      Incomplete
                    </span>
                  )}
                  {canEdit && !quest.is_visible && (
                    <span style={{ fontSize: '0.7rem', background: '#6b7f91', color: 'var(--text-primary)', padding: '0.2rem 0.5rem', borderRadius: '4px', fontWeight: 600 }}>Hidden</span>
                  )}
                </div>
              )}
              <div style={{ color: 'var(--text-secondary)', lineHeight: 1.6, fontSize: '0.9rem', display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                {isHidden ? 'This quest has not been revealed yet.' : <MarkdownText>{quest.description}</MarkdownText>}
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: '0.5rem', marginTop: 'auto', paddingTop: '0.75rem' }}>
                {!isHidden ? <span style={{ color: 'var(--text-muted)', fontSize: '0.8rem', fontStyle: 'italic' }}>(click to see more)</span> : <span />}
                {canEdit && (
                  <div onClick={(e) => e.stopPropagation()} style={{ flexShrink: 0 }}>
                    <AdminControls
                      item={quest}
                      onEdit={handleEdit}
                      onDelete={handleDelete}
                      onToggleVisibility={handleToggleVisibility}
                      type="quest"
                    />
                  </div>
                )}
              </div>
            </div>
          )
        })}
      </div>
      )}

      {selectedQuest && (() => {
        const isHidden = !selectedQuest.is_visible && !canEdit

        return (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(5, 10, 18, 0.95)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 9999,
              padding: '1rem'
            }}
          >
            <div
              style={{
                background: 'linear-gradient(135deg, var(--primary-blue) 0%, var(--secondary-dark) 100%)',
                border: '2px solid var(--accent-teal)',
                borderRadius: '12px',
                maxWidth: '980px',
                width: '100%',
                boxShadow: '0 10px 40px rgba(0, 0, 0, 0.6)',
                position: 'relative',
                maxHeight: '85vh',
                display: 'flex',
                flexDirection: 'column',
                // See SessionsTab for why padding moved onto the children.
                overflow: 'hidden'
              }}
            >
              {/* Non-scrolling header row — an absolutely-positioned Close
                  button would sit on top of the scroll wrapper's scrollbar. */}
              <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '1rem 1rem 0.5rem', flexShrink: 0 }}>
                <button
                  onClick={() => setSelectedQuest(null)}
                  style={{
                    background: 'linear-gradient(135deg, #3a4550 0%, #2d363f 100%)',
                    color: '#ffffff',
                    border: '1px solid #5a6570',
                    borderRadius: '8px',
                    padding: '0.6rem 1.4rem',
                    cursor: 'pointer',
                    fontSize: '1rem',
                    fontWeight: 600
                  }}
                  aria-label="Close quest details"
                >
                  Close
                </button>
              </div>

              <div style={{ overflowY: 'auto', minHeight: 0, padding: '0 1rem 2rem 2rem', scrollbarGutter: 'stable' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
                <div>
                  <h2 style={{ color: 'var(--text-primary)', marginBottom: '0.5rem', textDecoration: selectedQuest.completed ? 'line-through' : 'none' }}>
                    {isHidden ? 'Unknown Quest' : selectedQuest.title}
                  </h2>
                  {!isHidden && (
                    <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                      <span style={{ background: selectedQuest.quest_type === 'main' ? 'var(--accent-teal)' : '#6b7f91', color: 'var(--text-primary)', padding: '0.25rem 0.6rem', borderRadius: '4px', fontSize: '0.75rem', fontWeight: 600 }}>
                        {selectedQuest.quest_type.charAt(0).toUpperCase() + selectedQuest.quest_type.slice(1)} Quest
                      </span>
                      {selectedQuest.priority === 'high' && (
                        <span style={{ background: '#c0392b', color: 'var(--text-primary)', padding: '0.25rem 0.6rem', borderRadius: '4px', fontSize: '0.75rem', fontWeight: 600 }}>
                          High Priority
                        </span>
                      )}
                      {selectedQuest.completed === 1 && (
                        <span style={{ background: '#27ae60', color: 'var(--text-primary)', padding: '0.25rem 0.6rem', borderRadius: '4px', fontSize: '0.75rem', fontWeight: 600 }}>
                          ✓ Completed
                        </span>
                      )}
                      {canEdit && !selectedQuest.is_visible && (
                        <span style={{ background: '#6b7f91', color: 'var(--text-primary)', padding: '0.25rem 0.6rem', borderRadius: '4px', fontSize: '0.75rem', fontWeight: 600 }}>
                          Hidden from Players
                        </span>
                      )}
                    </div>
                  )}
                </div>

                <div style={{ color: 'var(--text-secondary)', lineHeight: 1.7, fontSize: '1rem' }}>
                  {isHidden ? 'This quest has not been revealed yet.' : <MarkdownText style={{ whiteSpace: 'pre-line' }}>{selectedQuest.full_description || selectedQuest.description}</MarkdownText>}
                </div>

                {!isHidden && (
                  <QuestSteps
                    steps={parseSteps(selectedQuest.steps)}
                    editable={canEdit}
                    onSetStatus={(i, status) => handleStepStatus(selectedQuest, i, status)}
                  />
                )}
              </div>
              </div>
            </div>
          </div>
        )
      })()}

      <QuestForm
        isOpen={isCreating || !!editingQuest}
        onClose={() => {
          setIsCreating(false)
          setEditingQuest(null)
        }}
        quest={editingQuest}
        onSubmit={handleFormSubmit}
      />
    </div>
  )
}

export default QuestsTab
